import { z } from 'zod';
/**
 * Job settings, status, counters and the job summary the list screens read.
 *
 * Split out of contract.ts, which carried the whole API contract in one file.
 * Nothing here changed on the way out.
 */
// ---------- Job settings -------------------------------------------------------
export const PersonaGroupSchema = z.object({
    key: z.string(),
    label: z.string(),
    titleQuery: z.string().max(255),
    pageSize: z.number().int().min(1).max(10),
});
export const JobSettingsSchema = z.object({
    presetId: z.string().default('founders_and_tech'),
    personas: z.array(PersonaGroupSchema).min(1),
    /**
     * Accepted people go to Zoho CRM as they are accepted, whatever the account's
     * push mode. Set on a job started from a CRM view: its companies came from
     * the CRM, so its people belong there.
     */
    crmPush: z.boolean().default(false),
    minPeople: z.number().int().min(1).max(10).default(1),
    includeCompanyProfile: z.boolean().default(true),
    includeDirector: z.boolean().default(true),
    autoWorkEmail: z.boolean().default(false),
    expectedCountries: z.array(z.string().length(2)).default(['IN']),
    creditCapPerCompany: z.number().int().min(1).max(200).default(25),
    /**
     * Credits one job may spend before it stops itself.
     *
     * 20,000 leaves a full 1,000-company batch real headroom: the measured cost is
     * roughly 8.4 credits per company, so a complete batch lands near 8,400 and a
     * cap that merely matched it would halt honest jobs on ordinary variance. It
     * is a runaway guard, not a budget — the thing standing between a mistyped
     * list and an emptied account.
     */
    creditCapTotal: z.number().int().min(1).default(20000),
    enableFetch: z.boolean().default(true),
    /**
     * Bypass the reuse cache and re-buy every company in this job.
     *
     * Off by default: profiling the same company twice costs the same credits as
     * the first time, and the marketing team re-uploads overlapping lists. Turn it
     * on when a list is deliberately being refreshed.
     */
    forceRefresh: z.boolean().default(false),
});
export const COLUMN_ROLES = [
    'company_name',
    'website',
    'linkedin_url',
    'country',
    'target_roles',
    'passthrough',
];
export const ColumnMappingSchema = z.record(z.string(), z.enum(COLUMN_ROLES));
// ---------- Job ----------------------------------------------------------------
export const JOB_STATUS = [
    'DRAFT',
    'READY',
    'RUNNING',
    'PAUSED',
    'COMPLETED',
    'COMPLETED_WITH_ERRORS',
    'CANCELLED',
    'FAILED',
];
/**
 * The statuses a job can be deleted in.
 *
 * A job that ran is the record of what was spent and on whom — what an invoice
 * is checked against, and what a contact's provenance points back to. None of
 * those are deletable, at any age, by anyone. These three are the ones that
 * never bought anything: an upload nobody confirmed, a configured job nobody
 * started, and a run stopped before it produced a contact.
 */
export const DELETABLE_JOB_STATUSES = ['DRAFT', 'READY', 'CANCELLED'];
export function isDeletableJob(status) {
    return DELETABLE_JOB_STATUSES.includes(status);
}
export const JobCountersSchema = z.object({
    total: z.number().default(0),
    processed: z.number().default(0),
    succeeded: z.number().default(0),
    failed: z.number().default(0),
    noPeople: z.number().default(0),
    contactsFound: z.number().default(0),
    contactsNew: z.number().default(0),
    needsReview: z.number().default(0),
    /**
     * How many review items have been decided, ever. Monotonic, never recomputed.
     *
     * `needsReview` cannot be derived from the company rows alone. A row records
     * how many candidates it QUEUED for review, which never changes; the queue
     * itself shrinks as people and the model work through it. The housekeeping
     * reconciler recomputes counters from the rows, so without this it put every
     * resolved item back — a reviewer working on a job that was still running
     * watched their own decisions undone every five minutes, and three jobs ended
     * up overstating their queue by 80 items between them.
     *
     * Open items are the difference: queued minus decided.
     */
    reviewsResolved: z.number().default(0),
    creditsEstimated: z.number().default(0),
    creditsUsed: z.number().default(0),
});
//# sourceMappingURL=jobs.js.map